/** @file apiAdapter: the api client as the editor's EditorApi (projects = games, puppets = characters). */
import type { Pose3DClient } from '@zilo/pose3d-api/client/createClient';
import type { EditorApi, ProjectCalls, ProjectEntry, PuppetCalls, PuppetEntry, WirePuppetDoc } from './editorApi';

type AnyFn = (...args: unknown[]) => Promise<unknown>;
type Members = Record<string, unknown>;
type PerPuppet = Pick<PuppetCalls, (typeof PER_PUPPET)[number]>;

const PER_PUPPET = ['commands', 'undo', 'redo', 'revisions', 'exportBundle', 'lint', 'slots', 'slotMask', 'uploadImage', 'templateClipsImport', 'live'] as const;

const fnOf = (owner: unknown, name: string): AnyFn | undefined => {
  const f = (owner as Members | undefined)?.[name];
  return typeof f === 'function' ? (f as AnyFn) : undefined;
};

/** Calls `f`, or rejects saying the server lacks `what` (an older server, or a call the client does not have). */
const call = <T>(f: AnyFn | undefined, what: string, ...args: unknown[]): Promise<T> =>
  f === undefined ? Promise.reject(new Error(`${what}: not offered by this server (update it)`)) : (f(...args) as Promise<T>);

const asProject = (p: Partial<ProjectEntry> & { id: string; name: string }): ProjectEntry => ({
  id: p.id, name: p.name, defaults: p.defaults ?? {}, puppetCount: p.puppetCount ?? 0, updatedAt: p.updatedAt ?? 0, trashedAt: p.trashedAt ?? null,
});

const asPuppet = (p: Omit<PuppetEntry, 'project' | 'updatedAt' | 'trashedAt'> & Partial<PuppetEntry>, projectId: string): PuppetEntry =>
  ({ ...p, project: p.project ?? projectId, updatedAt: p.updatedAt ?? 0, trashedAt: p.trashedAt ?? null });

/** The per-puppet calls: `client.puppets.<name>` when the client has it, else the flat `client.<name>`. */
function perPuppet(c: Members, owner: unknown): PerPuppet {
  const pick = (name: string) => (...args: unknown[]) => call(fnOf(owner, name) ?? fnOf(c, name), name, ...args);
  return Object.fromEntries(PER_PUPPET.map((n) => [n, pick(n)])) as unknown as PerPuppet;
}

/** W6: `client.projects.*` are the collections, `client.puppets.*` the per-character calls (T-602). */
function modern(c: Members): Pick<EditorApi, 'projects' | 'puppets'> {
  const pr = c['projects'];
  const pu = c['puppets'];
  const projects: ProjectCalls = {
    list: async (q = {}) => (await call<ProjectEntry[]>(fnOf(pr, 'list'), 'projects.list', q.trashed === true ? { trashed: true } : {})).map(asProject),
    create: (body) => call(fnOf(pr, 'create'), 'projects.create', body),
    get: async (id) => asProject(await call<ProjectEntry>(fnOf(pr, 'get'), 'projects.get', id)),
    rename: async (id, name) => {
      const rename = fnOf(pr, 'rename');
      await (rename === undefined ? call(fnOf(pr, 'update') ?? fnOf(pr, 'patch'), 'projects.rename', id, { name }) : rename(id, name));
    },
    trash: (id) => call(fnOf(pr, 'trash'), 'projects.trash', id),
    restore: (id) => call(fnOf(pr, 'restore'), 'projects.restore', id),
    purge: (id) => call(fnOf(pr, 'purge'), 'projects.purge', id),
  };
  const puppets: PuppetCalls = {
    ...perPuppet(c, pu),
    list: async (q) => (await call<PuppetEntry[]>(fnOf(pu, 'list'), 'puppets.list', q)).map((p) => asPuppet(p, q.project)),
    create: (body) => call(fnOf(pu, 'create'), 'puppets.create', body),
    get: async (id) => {
      const doc = await call<Omit<WirePuppetDoc, 'project'> & { project?: string | null }>(fnOf(pu, 'get'), 'puppets.get', id);
      return { ...doc, project: doc.project ?? null };
    },
    trash: (id) => call(fnOf(pu, 'trash'), 'puppets.trash', id),
    restore: (id) => call(fnOf(pu, 'restore'), 'puppets.restore', id),
    purge: (id) => call(fnOf(pu, 'purge'), 'puppets.purge', id),
  };
  return { projects, puppets };
}

/** The api client (`client.projects.*` = games, `client.puppets.*` = characters, T-602) as the editor's EditorApi. */
export function apiAdapter(client: Pose3DClient): EditorApi {
  const c = client as unknown as Members;
  const shaped = modern(c);
  return {
    url: client.url,
    ...shaped,
    templates: () => client.templates(),
    me: () => client.me(),
    login: (password) => client.login(password),
    logout: () => client.logout(),
  };
}
